import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../../services/adminApiService';
import { Calendar, Clock, AlertTriangle, CheckCircle2, RefreshCw, ArrowRight } from 'lucide-react';

export const AdminUpcomingPaymentsView: React.FC = () => {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<'today' | 'in3Days' | 'in7Days' | 'in30Days'>('in7Days');

  const loadUpcoming = async () => {
    setLoading(true);
    try {
      const res = await adminApiService.getUpcomingPayments();
      if (res.success) {
        setData(res);
      }
    } catch (err) {
      console.error('Failed to load upcoming payments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUpcoming();
  }, []);

  const counts = data?.counts || { today: 0, in3Days: 0, in7Days: 0, in30Days: 0 };
  const currentList = data?.categorized ? data.categorized[activeCategory] || [] : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-600" />
            <span>Yaklaşan Yenilemeler & Tahsilat Hattı</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Günü gelen, 3 gün, 7 gün ve 30 gün içinde yenilenecek müşteri aboneliklerini ve tahsilat takvimini takip edin.
          </p>
        </div>
        <button
          onClick={loadUpcoming}
          disabled={loading}
          className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition flex items-center gap-2 self-start"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Yenile</span>
        </button>
      </div>

      {/* Category Pills */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button
          onClick={() => setActiveCategory('today')}
          className={`p-4 rounded-2xl border text-left transition ${
            activeCategory === 'today'
              ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-500/20'
              : 'bg-white border-slate-200 hover:bg-slate-50'
          }`}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-rose-600">Bugün Bitenler</div>
          <div className="text-2xl font-black text-rose-900 mt-1">{counts.today}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Acil Yenileme</div>
        </button>

        <button
          onClick={() => setActiveCategory('in3Days')}
          className={`p-4 rounded-2xl border text-left transition ${
            activeCategory === 'in3Days'
              ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-500/20'
              : 'bg-white border-slate-200 hover:bg-slate-50'
          }`}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-600">3 Gün İçinde</div>
          <div className="text-2xl font-black text-amber-900 mt-1">{counts.in3Days}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Kritik Süreç</div>
        </button>

        <button
          onClick={() => setActiveCategory('in7Days')}
          className={`p-4 rounded-2xl border text-left transition ${
            activeCategory === 'in7Days'
              ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-500/20'
              : 'bg-white border-slate-200 hover:bg-slate-50'
          }`}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-blue-600">7 Gün İçinde</div>
          <div className="text-2xl font-black text-blue-900 mt-1">{counts.in7Days}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Haftalık Takvim</div>
        </button>

        <button
          onClick={() => setActiveCategory('in30Days')}
          className={`p-4 rounded-2xl border text-left transition ${
            activeCategory === 'in30Days'
              ? 'bg-emerald-50 border-emerald-300 ring-2 ring-emerald-500/20'
              : 'bg-white border-slate-200 hover:bg-slate-50'
          }`}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">30 Gün İçinde</div>
          <div className="text-2xl font-black text-emerald-900 mt-1">{counts.in30Days}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Aylık Projeksiyon</div>
        </button>
      </div>

      {/* Selected Category List */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Web Sitesi / Hesap</th>
                <th className="py-3 px-4">Müşteri</th>
                <th className="py-3 px-4">Hizmet / Paket</th>
                <th className="py-3 px-4">Yenileme Tutarı</th>
                <th className="py-3 px-4">Yenileme Tarihi</th>
                <th className="py-3 px-4">Kalan Süre</th>
                <th className="py-3 px-4">Durum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-emerald-500" />
                    Yenileme verileri yükleniyor...
                  </td>
                </tr>
              ) : currentList.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    Bu zaman aralığında yenilenecek abonelik bulunmuyor.
                  </td>
                </tr>
              ) : (
                currentList.map((item: any) => (
                  <tr key={item.id} className="hover:bg-slate-50/70 transition">
                    <td className="py-3.5 px-4 font-semibold text-slate-900">
                      {item.siteName}
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="text-slate-800 font-medium">{item.customerName}</div>
                      <div className="text-[11px] text-slate-400">{item.customerEmail}</div>
                    </td>
                    <td className="py-3.5 px-4 text-slate-700">
                      {item.service}
                    </td>
                    <td className="py-3.5 px-4 font-black text-slate-900">
                      {Number(item.amount).toLocaleString('tr-TR')} {item.currency}
                    </td>
                    <td className="py-3.5 px-4 text-slate-700 font-mono text-[11px]">
                      {item.renewalDateFormatted}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                        item.remainingDays <= 1
                          ? 'bg-rose-100 text-rose-800'
                          : item.remainingDays <= 3
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {item.remainingDays === 0 ? 'Bugün Bitiyor' : `${item.remainingDays} gün kaldı`}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
                        <CheckCircle2 className="w-3 h-3" />
                        Aktif
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
